// Transactions · TransactionDocsBucket
//
// One shared bucket renderer used in BOTH the pipeline/queue table cells AND
// the entity-view "Dokumenty" section. Same component, same data shape, same
// pixel-for-pixel look as the pre-migration `DocumentPillStrip` + `PhotoPill`
// + `DocumentsSection` flow.
//
// Three modes:
//
//   - "uploaded" — one `<Pill>` per `document_type`, worst-wins tone
//     (pending → amber, not_approved → red, approved → green). Label is
//     `{DOC_TYPE_LABEL[type]} ×N`. Click opens the review modal for that
//     type. Trailing dashed-border ghost pill upload (drag-drop, red when
//     `checkPending`).
//
//   - "photos" — one aggregated `<Pill>` with inline status counts
//     "✓N ⏳N ✗N". Worst-wins tone. Click opens the photo review modal.
//     Trailing dashed-border ghost pill upload (camera icon).
//
//   - "generated" — per-expected-type pill (CMR / Aneks / Zlecenie) with
//     present/missing state and a regenerate secondary action.
//
// Data flow:
//   - `files`/`transactionId`/`defs`/etc. come in as props (the host owns
//     the fetch — pipeline cells get rows from `ctx.sub("files")`, the
//     view section uses `useSubQueryOptional` + `useRunListQuery`).
//   - Actions (upload, regenerate, review-modal-open) are read from the
//     ambient TransactionWorkflow context via `useTransactionWorkflowOptional`.
//     When the workflow provider is mounted (pipeline pages, transaction
//     view page, sales/logistics queues) every affordance lights up; when
//     not, pills render read-only (no upload, no review-open, no regenerate)
//     so the same component is safe to drop into any surface.

import React, { useRef, useState, useCallback } from "react";
import {
  CameraOutlined,
  EyeOutlined, LoadingOutlined, PlusOutlined, ReloadOutlined,
} from "@ant-design/icons";
import type { DocumentType } from "@shared/types";
import type { TransactionFile, Transport } from "@shared/types/transaction";

import { Pill } from "@ds/display/Pill";
import { DocumentPill } from "@ds/tables/cells/DocumentPill";
import { worstReviewTone, type ReviewTone } from "@ds/tables/cells/DocumentPill.helpers";
import { Inline } from "@ds/layout/Inline";
import { Stack } from "@ds/layout/Stack";
import { FONT_SIZE, TONE, SURFACE, TEXT, RADIUS } from "@ds/tokens/palette";

import {
  useTransactionWorkflowOptional,
} from "../hooks/transactionWorkflowContext";

// ─── Constants (legacy DOC_TYPE_LABEL preserved verbatim) ──────────────────

const DOC_TYPE_LABEL: Partial<Record<DocumentType, string>> = {
  ie529:             "IE529",
  ie599:             "IE599",
  cmr:               "CMR",
  annex7:            "Aneks VII",
  weight_tickets:    "Kwit wagowy",
  waste_image:       "Zdjęcie",
  transport_order:   "Zlecenie",
  transport_invoice: "FV transportu",
  other:             "Inne",
};

const ACCEPT_DOCS   = ".pdf,.jpg,.jpeg,.png,.tiff";
const ACCEPT_PHOTOS = "image/*,.heic,.heif";

// ─── Generated-doc expected-types shape ────────────────────────────────────

export interface GeneratedDocDef {
  type: DocumentType;
  label: string;
  /** Optional checklist `*_check` status — when "not_required" the pill is
   *  hidden so we don't pester for docs that don't apply to this transaction. */
  checkStatus?: string | null;
}

// ─── Inputs ────────────────────────────────────────────────────────────────

export type DocsBucketMode = "uploaded" | "photos" | "generated";

export interface TransactionDocsBucketInput {
  files: TransactionFile[];
  transactionId: number;
  mode: DocsBucketMode;
  /** Pill arrangement. `"inline"` (default) = wrapping row; `"stack"` = one pill per line. */
  layout?: "inline" | "stack";
  /** Tints the ghost upload pill red when the matching checklist check is pending. */
  checkPending?: boolean;
  /** "uploaded" only — passed through to `actions.uploadDocs`. */
  uploadStatus?: "copy" | "original";
  /** "uploaded" only — the transaction reference, threaded to `actions.uploadDocs` to trigger Gemini scan extraction. */
  referenceNumber?: string;
  /** "uploaded" only — routes uploads to the customs-type-picker handler. */
  useCustomsTypePicker?: boolean;
  /** "generated" only — expected doc types regardless of `files`. */
  defs?: ReadonlyArray<GeneratedDocDef>;
  /** "generated" + transport_order only — picks the first truck leg's id. */
  transports?: Transport[];
  /** "generated" only — gate: only allows regenerate when transport is complete. */
  transportDone?: boolean;
}

export type TransactionDocsBucketProps = TransactionDocsBucketInput;

// ─── Helpers ───────────────────────────────────────────────────────────────

function driveUrl(file: TransactionFile): string {
  return file.document_status === "generated"
    ? `https://docs.google.com/document/d/${file.drive_file_id}/edit`
    : `https://drive.google.com/file/d/${file.drive_file_id}/view`;
}

// ─── GhostPill — dashed-border drag-drop upload affordance ─────────────────
//
// Mirrors the legacy `GhostPill` (src/pages/transactions/components/cells/
// GhostPill.tsx) but rendered against current @ds tokens. Inline here
// instead of a separate file — the only consumer is this bucket renderer.

interface GhostPillProps {
  pending?: boolean;
  accept: string;
  onFiles: (files: File[]) => void;
  children: React.ReactNode;
  /**
   * Stable testId for the hidden `<input type="file">`. e2e-ui specs use
   * `page.setInputFiles(...)` against this id to drive uploads. Pattern:
   * `ghost-pill-input-{bucket}` where `bucket` is the docs bucket name.
   */
  inputTestId?: string;
}

function fileMatchesAccept(file: File, accept: string): boolean {
  const tokens = accept.split(",").map((s) => s.trim().toLowerCase());
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();
  return tokens.some((tok) => {
    if (tok.startsWith(".")) return name.endsWith(tok);
    if (tok.endsWith("/*"))  return mime.startsWith(tok.slice(0, -1));
    return mime === tok;
  });
}

function GhostPill({ pending, accept, onFiles, children, inputTestId }: GhostPillProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [hover, setHover] = useState(false);
  const enterCount = useRef(0);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) onFiles(files);
    e.target.value = "";
  }, [onFiles]);

  const onDrop = useCallback((files: File[]) => {
    const accepted = files.filter((f) => fileMatchesAccept(f, accept));
    if (accepted.length > 0) onFiles(accepted);
  }, [accept, onFiles]);

  const borderColor = pending ? TONE.red.base : SURFACE.border;
  const textColor   = pending ? TONE.red.base : TEXT.muted;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        style={{ display: "none" }}
        onChange={handleInputChange}
        data-testid={inputTestId}
      />
      <span
        role="button"
        tabIndex={0}
        style={{
          display:       "inline-flex",
          alignItems:    "center",
          gap:           4,
          padding:       "2px 9px",
          height:        22,
          borderRadius:  RADIUS.pill,
          // Hover deepens to a soft wash; drag-over keeps the blue affordance.
          background:    dragOver
                           ? TONE.blue.softBg
                           : hover
                             ? (pending ? TONE.red.softBg : SURFACE.surfaceHover)
                             : "transparent",
          border:        `1px dashed ${
                           dragOver
                             ? TONE.blue.base
                             : hover
                               ? (pending ? TONE.red.base : TEXT.muted)
                               : borderColor
                         }`,
          color:         hover && !pending ? TEXT.primary : textColor,
          fontSize:      FONT_SIZE.micro,
          cursor:        "pointer",
          whiteSpace:    "nowrap",
          transition:    "border-color 0.15s, background-color 0.15s, color 0.15s",
        }}
        onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onDragEnter={(e) => {
          e.preventDefault(); e.stopPropagation();
          enterCount.current += 1;
          if (enterCount.current === 1) setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault(); e.stopPropagation();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDragLeave={(e) => {
          e.preventDefault(); e.stopPropagation();
          enterCount.current -= 1;
          if (enterCount.current <= 0) { enterCount.current = 0; setDragOver(false); }
        }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation();
          enterCount.current = 0; setDragOver(false);
          const files = Array.from(e.dataTransfer.files);
          if (files.length > 0) onDrop(files);
        }}
      >
        {children}
      </span>
    </>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────

export function TransactionDocsBucket({
  files, transactionId, mode, layout = "inline",
  checkPending = false, uploadStatus, referenceNumber, useCustomsTypePicker,
  defs, transports, transportDone,
}: TransactionDocsBucketProps): React.ReactElement | null {
  // `"stack"` lays each pill on its own line (table cells); `"inline"` wraps in a
  // row (the entity-view "Dokumenty" section, where each bucket is already a KV row).
  // Plain function (not a component) so the pill subtree keeps its identity — the
  // GhostPill's in-flight drag/hover state must survive re-renders.
  const wrapPills = (children: React.ReactNode): React.ReactElement =>
    layout === "stack"
      ? <Stack gap="xs" align="start">{children}</Stack>
      : <Inline gap="xs" align="center" wrap>{children}</Inline>;

  // Workflow is the single source of upload + regenerate + review handlers.
  // Optional so the component is safe to render where the provider isn't
  // mounted (e.g. detached previews) — handlers no-op there.
  const workflow = useTransactionWorkflowOptional();
  const actions  = workflow?.actions;
  const external = workflow?.external;

  const uploading = !!actions?.uploadingKeys?.has(
    `${transactionId}:${mode === "photos" ? "photos" : "docs"}`,
  );

  const doUpload = (picked: File[]): void => {
    if (!actions || picked.length === 0) return;
    if (mode === "photos") {
      void actions.uploadPhotos(transactionId, picked);
    } else if (useCustomsTypePicker) {
      actions.uploadCustomsDocs(transactionId, picked);
    } else {
      void actions.uploadDocs(transactionId, picked, uploadStatus, referenceNumber);
    }
  };

  const onReviewDocs = (revFiles: TransactionFile[]): void => {
    if (!external) return;
    if (mode === "photos") {
      (external.onReviewPhotos ?? external.onReviewDocs)(transactionId, revFiles);
    } else {
      external.onReviewDocs(transactionId, revFiles);
    }
  };

  if (uploading) {
    return (
      <Pill tone="blue" variant="soft" size="sm" icon={<LoadingOutlined />}>
        {""}
      </Pill>
    );
  }

  // ── Mode: generated ─────────────────────────────────────────────────────
  if (mode === "generated") {
    const expected = defs ?? [];
    const presentByType = new Map<string, TransactionFile>(
      files.map((f) => [f.document_type, f]),
    );
    return wrapPills(
      expected.map((d) => {
          if (d.checkStatus === "not_required") return null;
          const file = presentByType.get(d.type);
          const isPresent = !!file;

          const transportId = d.type === "transport_order"
            ? (transports ?? [])
                .filter((tp) => tp.transport_type === "truck")
                .sort((a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0))[0]?.id
            : undefined;
          const genKey = transportId
            ? `${transactionId}-${d.type}-${transportId}`
            : `${transactionId}-${d.type}`;
          const isGenerating = actions?.generatingKeysRef.current?.has(genKey) ?? false;
          const canRegenerate = !!actions && !!transportDone
            && (d.type !== "transport_order" || !!transportId);

          return (
            <DocumentPill
              key={d.type}
              state={isPresent ? "present" : "missing"}
              reviewTone={isPresent ? "approved" : "none"}
              label={d.label}
              icon={<EyeOutlined style={{ fontSize: FONT_SIZE.label }} />}
              onOpen={isPresent && file
                ? () => window.open(driveUrl(file), "_blank")
                : undefined}
              secondaryAction={actions ? {
                kind: "regenerate",
                icon: isGenerating
                  ? <LoadingOutlined />
                  : <ReloadOutlined style={{ fontSize: FONT_SIZE.label }} />,
                onClick: () => {
                  if (canRegenerate) {
                    void actions.generateDoc(transactionId, d.type, transportId);
                  }
                },
                title: isPresent ? "Wygeneruj ponownie" : "Wygeneruj",
                testId: `doc-status-pill-generate-${d.type}`,
              } : undefined}
            />
          );
        }),
    );
  }

  // ── Mode: photos ────────────────────────────────────────────────────────
  if (mode === "photos") {
    const approved = files.filter((f) => f.review_status === "approved").length;
    const pending  = files.filter((f) => f.review_status === "pending").length;
    const rejected = files.filter((f) => f.review_status === "not_approved").length;
    const total    = files.length;

    const reviewTone: ReviewTone =
      pending > 0 ? "pending" : rejected > 0 ? "not_approved" : "approved";

    return wrapPills(
      <>
        {total > 0 && (
          <DocumentPill
            label={String(total)}
            state="present"
            reviewTone={reviewTone}
            tally={{ approved, pending, rejected }}
            testId={`docs-bucket-photos-pill-${transactionId}`}
            onOpen={() => onReviewDocs(files)}
          />
        )}
        {actions && (
          <GhostPill
            pending={checkPending}
            accept={ACCEPT_PHOTOS}
            onFiles={doUpload}
            inputTestId={`ghost-pill-input-${transactionId}-photos`}
          >
            <CameraOutlined />
          </GhostPill>
        )}
      </>,
    );
  }

  // ── Mode: uploaded ──────────────────────────────────────────────────────
  const byType = new Map<DocumentType, TransactionFile[]>();
  for (const f of files) {
    const t = f.document_type;
    const arr = byType.get(t);
    if (arr) arr.push(f); else byType.set(t, [f]);
  }

  return wrapPills(
    <>
      {Array.from(byType.entries()).map(([type, typeFiles]) => {
        const reviewTone = worstReviewTone(typeFiles.map((f) => f.review_status));
        const label = DOC_TYPE_LABEL[type] ?? type;
        const count = typeFiles.length;
        return (
          <DocumentPill
            key={type}
            state="present"
            reviewTone={reviewTone}
            label={label}
            count={count}
            testId={`cell-review-${type}-${transactionId}`}
            onOpen={() => onReviewDocs(typeFiles)}
          />
        );
      })}
      {actions && (
        <GhostPill
          pending={checkPending}
          accept={ACCEPT_DOCS}
          onFiles={doUpload}
          inputTestId={
            useCustomsTypePicker
              ? `ghost-pill-input-${transactionId}-customs`
              : uploadStatus === "copy"
                ? `ghost-pill-input-${transactionId}-copies`
                : `ghost-pill-input-${transactionId}-originals`
          }
        >
          <PlusOutlined />
        </GhostPill>
      )}
    </>,
  );
}
